import React, { FC, MouseEvent, MouseEventHandler } from 'react';
import styled from 'styled-components/macro';

import Icon from 'components/Icon';
import Button from 'components/Button';
import Typography from 'components/Typography';
import { PlanType } from 'components/MembershipRing';
import { ColorVariants } from 'theme';
import {
    PlanRoot,
    PlanBody,
    PlanFeatures,
    PlanHeadline,
    PlanHeader,
} from 'components/PlanCommon';

const PlanFullPrice = styled((props) => (
    <Typography
        data-type="full"
        variant="bodym"
        color="baseGray"
        align="center"
        {...props}
    />
))`
    font-size: 1.5rem;
    text-decoration: line-through;
`;

// It wraps Button in order to match other cards' CTA styles
const CurrentMembershipLabel = styled((props) => (
    <Button role="banner" block spaceTop="mbase" {...props} />
))`
    cursor: default;
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.darkGrey};

    & span:last-child {
        margin-left: ${({ theme }) => theme.space.sbase};
    }
`;

export type PlanGradientProps = {
    onPlanClick?: MouseEventHandler<HTMLDivElement>;

    price?: any;
    fullprice?: any;

    ctaPrints?: string;
    pricePrints?: string;

    cta?: string;

    description: {
        title: string;
        subtitle?: string;
    };

    currentPlan?: boolean;
    currentPlanText?: string;

    headline?: string;
    method?: 'upgrade' | 'resubscribe' | 'subscribe';

    planType?: PlanType;
    features?: Array<{ name: string; available: boolean }>;

    biggerCta?: boolean;
};

const PlanGradient: FC<PlanGradientProps> = ({
    biggerCta,
    onPlanClick,
    ctaPrints,
    pricePrints,
    price,
    fullprice,
    cta,
    description,
    currentPlan,
    headline,
    method,
    features,
    planType,
    currentPlanText,
}) => {
    const handlePlanClick = (e: MouseEvent<HTMLDivElement>) => {
        if (!(method === 'upgrade' && currentPlan) && onPlanClick) {
            onPlanClick(e);
        }
    };

    const shouldShowTextInsteadCTA =
        method && method !== 'resubscribe' && currentPlan;

    return (
        <PlanRoot onClick={handlePlanClick} $height="100%">
            {headline && (
                <PlanHeadline $color={planType as ColorVariants}>
                    <Typography
                        align="center"
                        variant="bodys"
                        fontWeight={500}
                        color="white"
                    >
                        {headline}
                    </Typography>
                </PlanHeadline>
            )}

            <PlanBody>
                {description.subtitle && (
                    <Typography
                        align="center"
                        variant="bodyxs"
                        transform="uppercase"
                        color="baseGray"
                        fontWeight={500}
                    >
                        {description.subtitle}
                    </Typography>
                )}

                <PlanHeader
                    align="center"
                    variant="h4"
                    spaceBottom="small"
                    $color={planType as ColorVariants}
                    dangerouslySetInnerHTML={{
                        __html: description.title,
                    }}
                />

                {fullprice && (
                    <PlanFullPrice>
                        <span>{fullprice.currency}</span>
                        <span>{fullprice.amount}</span>{' '}
                        {fullprice.period && (
                            <span className="Period_sc">
                                {fullprice.period}
                            </span>
                        )}
                    </PlanFullPrice>
                )}

                {price && (
                    <Typography
                        variant="bodyl"
                        align="center"
                        fontWeight={700}
                        data-type="actual"
                    >
                        <span>{price.currency}</span>
                        <span className="Amount_sc">{price.amount}</span>{' '}
                        {price.period && (
                            <span className="Period_sc">{price.period}</span>
                        )}
                    </Typography>
                )}

                {pricePrints && (
                    <Typography
                        data-type="prints"
                        variant="bodyxs"
                        align="center"
                        color="baseGray"
                    >
                        {pricePrints}
                    </Typography>
                )}

                {shouldShowTextInsteadCTA && (
                    <CurrentMembershipLabel>
                        <Icon name="checkmark" size={16} />
                        <span>{currentPlanText}</span>
                    </CurrentMembershipLabel>
                )}

                {!shouldShowTextInsteadCTA && cta && (
                    <React.Fragment>
                        <Button
                            fontSize={biggerCta ? '1.125rem' : undefined}
                            data-type="select"
                            theme="primary"
                            block
                            spaceTop="mbase"
                        >
                            {cta}
                        </Button>
                        {ctaPrints && (
                            <Typography
                                data-type="prints"
                                variant="bodyxs"
                                align="center"
                                color="baseGray"
                                spaceTop="small"
                            >
                                {ctaPrints}
                            </Typography>
                        )}
                    </React.Fragment>
                )}

                <PlanFeatures>
                    {features &&
                        features.map(({ available, name }, i) => (
                            <div
                                className={`PItem_sc ${available ? '' : 'off'}`}
                                key={`plan-feature-${i}`}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: name,
                                    }}
                                />
                                {available && (
                                    <Icon
                                        className="ic_sc"
                                        name="checkmark"
                                        size={14}
                                    />
                                )}
                            </div>
                        ))}
                </PlanFeatures>
            </PlanBody>
        </PlanRoot>
    );
};

export default PlanGradient;
